import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { AppContext } from '../../../../contexts/AppContext';
import APIList from '../../../../http/ApiList';
import { FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID, SORT_SHOW } from '../../../../enum/common.enum';
import { groupBy, isNil, map, max, min, orderBy, sortBy, sumBy, uniq, uniqBy } from 'lodash';
import { getS3Object } from '@meteor/frontend-core';
import { Card, Flex, Space, Switch } from 'antd';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import ContentChart from '../../../base/content-chart';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

const CustomizeRiskChart: React.FC<any> = (props) => {
  const { property } = props;
  const { color, event$, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  const chartRef = useRef();
  // 完整数据
  const [dataProvider, setDataProvider] = useState({
    fields: [],
    targetData: [],
    currentData: [],
    currentRiskPotentialData: [],
  });
  // Loading
  const [chartLoading, setChartLoading] = useState<any>(true);
  // 比较KPI Fields Key
  const [compareKpiKey, setCompareKpiKey] = useState();
  // 比较对象
  const [compare, setCompare] = useState('snapshot');
  // 比较KPI Fields
  const fieldKpi = useMemo(() => {
    const { fields } = dataProvider;
    if (compareKpiKey) {
      return fields.find((field) => compareKpiKey === field.fieldKey) || {};
    }
    const findCompareKPI = fields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare);
    return findCompareKPI || {};
  }, [dataProvider, compareKpiKey]);
  // // Drill Down Fields
  // const drillFields = useMemo(() => dataProvider.fields.filter((field) => field.category === FIELD_CATEGORY.DRILL_DOWN), [dataProvider]);
  // 比较Drill Down Field
  const [compareFieldKey, setCompareFieldKey] = useState<any>();
  // Target Label
  const [tarLabel, setTarLabel] = useState();
  // Current Label
  const [curLabel, setCurLabel] = useState();
  //
  const [nameSortShow, setNameSortShow] = useState(false);
  /**
   * 调用API获取数据
   */
  const getChartData = (req) => {
    const {
      fiscalYear,
      compare,
      aggregationPeriod,
      drillSaveFields = [],
      metric,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
    } = req;

    const [startDate, endDate] = aggregationPeriod || [];

    const tarLabelMap = {
      snapshot: `断面合計(${targetSnapshot})`,
      period: `期間合計${targetPeriod.length > 0 ? `(${targetPeriod[0].format('YYYY-MM')} ~ ${targetPeriod[1].format('YYYY-MM')})` : ''}`,
      plan: label.plan,
      prev_year: '前年',
      prev_month: '前月',
    };
    const curLabelMap = {
      snapshot: `断面合計(${currentSnapshot})`,
      period: `期間合計${
        currentPeriod.length > 0 ? `(${currentPeriod[0].format('YYYY-MM')} ~ ${currentPeriod[1].format('YYYY-MM')})` : ''
      }`,
      plan: '合計',
      prev_year: '今年',
      prev_month: '今月',
    };
    setTarLabel(tarLabelMap[compare]);
    setCurLabel(curLabelMap[compare]);

    setChartLoading(true);
    setCompareKpiKey(metric);
    setCompareFieldKey(drillSaveFields.map((drillSaveField) => drillSaveField.fieldKey).join('&'));
    setCompare(compare);
    const paramMap = {
      snapshot: {
        snapshot: [currentSnapshot, targetSnapshot],
        compare: 'snapshot',
        reportType: 'monthly',
      },
      period: {
        snapshot: [currentSnapshot],
        compare: 'period',
        reportType: 'monthly',
        timeFilter: [
          {
            startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
          {
            startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      plan: {
        snapshot: [currentSnapshot],
        compare: 'plan',
        reportType: 'monthly',
      },
      prev_year: {
        snapshot: [currentSnapshot],
        compare: 'prev_year',
        reportType: 'monthly',
      },
      prev_month: {
        snapshot: [currentSnapshot],
        compare: 'prev_month',
        reportType: 'monthly',
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      fiscalYear,
      compare,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      drillFields: req.drillFields,
      ...paramMap[compare],
    };
    APIList.getRiskChartData()
      .post(params)
      .then(async (res) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        setDataProvider(response);
      })
      .finally(() => {
        setChartLoading(false);
      });
  };

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.RISK) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      getChartData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      getChartData(globalEvent.data);
    }
  });

  /**
   * ToolTip Format
   */
  const toolTipFormatter = (params) => {
    const [placeholder, decrease, increase, currentData, targetData] = params;
    const title = `<b>${decrease.name}</b>`;
    if (currentData && currentData.value) {
      return `${title}
        <div>${currentData.marker} ${currentData.seriesName}: ${kpiValue(currentData.value, fieldKpi.unitValue)}</div>
        ${
          !isNil(targetData)
            ? `<div>${targetData.marker} ${targetData.seriesName}: ${kpiValue(targetData.value, fieldKpi.unitValue)}</div>`
            : ''
        } 
      `;
    }
    return `${title}
      ${decrease.value ? `<div>${decrease.marker} ${decrease.name}: ${kpiValue(decrease.value, fieldKpi.unitValue)}</div>` : ''}
      ${increase.value ? `<div>${increase.marker} ${increase.name}: ${kpiValue(increase.value, fieldKpi.unitValue)}</div>` : ''}
      ${
        !isNil(targetData)
          ? `<div>${targetData.marker} ${targetData.seriesName}: ${kpiValue(targetData.value, fieldKpi.unitValue)}</div>`
          : ''
      } 
    `;
  };

  useEffect(() => {
    setChartLoading(true);
  }, [tralitSettings.account]);

  // Chart Option
  const options = useMemo(() => {
    const { fields, targetData, currentData, currentRiskPotentialData } = dataProvider;

    const riskPotentialFields = fields.filter(
      (field) => field.category === FIELD_CATEGORY.RISK_POTENTIAL && field.parentKey === compareKpiKey
    );

    const gbRiskCategoryData = groupBy(currentRiskPotentialData, 'riskCategory');
    const riskCategories = orderBy(
      Object.keys(gbRiskCategoryData),
      (key) => {
        if (!nameSortShow && riskPotentialFields.length > 0) {
          return sumBy(gbRiskCategoryData[key], riskPotentialFields[0].fieldKey);
        }
        return key;
      },
      [nameSortShow ? 'asc' : 'desc']
    );

    const riskPotentialLabels = riskCategories;

    const OTHER_LABEL = 'その他';

    const groupAndSummarize = (currentData, top: number) => {
      // Step 1: 按 dist 分组
      const groupFunc = (item) => {
        const groupKeys = compareFieldKey.split('&');
        return groupKeys.map((key) => item[toCamelCase(key)]).join('&');
      };
      const curGrouped = groupBy(currentData, groupFunc);
      // const tarGrouped = groupBy(targetData, groupFunc);
      // Step 2: 计算每个分组的 value 总和
      const summed = map(curGrouped, (curGroup, key) => {
        // const tarGroup = tarGrouped[key];
        return {
          [compareFieldKey]: key,
          value: sumBy(curGroup, toCamelCase(fieldKpi.fieldKey)),
        };
      });
      if (nameSortShow) {
        // Step 3: 按 absValue 降序排序，并取前6个
        const sorted = orderBy(summed, [compareFieldKey], ['asc']);
        const topData = sorted.slice(0, top);
        const rest = sorted.slice(top);
        // Step 4: 合并剩下的为 "その他"
        if (rest.length > 0) {
          const otherTotal = sumBy(rest, 'value');
          const result = [...topData, { [compareFieldKey]: OTHER_LABEL, value: otherTotal }];
          return result;
        }
        return topData;
      } else {
        // Step 3: 按 absValue 降序排序，并取前6个
        const sorted = orderBy(summed, ['value'], ['desc']);
        const topData = sorted.slice(0, top);
        const rest = sorted.slice(top);
        // Step 4: 合并剩下的为 "その他"
        if (rest.length > 0) {
          const otherTotal = sumBy(rest, 'value');
          const result = [...topData, { [compareFieldKey]: OTHER_LABEL, value: otherTotal }];
          return result;
        }
        return topData;
      }
    };

    const compareFieldInfos = groupAndSummarize(currentData, 6);
    const compareFieldAllValues = compareFieldInfos.map((compareFieldInfo) => compareFieldInfo[compareFieldKey]);

    const xAxisData = [...compareFieldAllValues, curLabel, ...riskPotentialLabels];
    const beforeTotal = sumBy(targetData, toCamelCase(fieldKpi.fieldKey));
    const afterTotal = sumBy(currentData, toCamelCase(fieldKpi.fieldKey));
    const beforeData = [...compareFieldAllValues.map(() => beforeTotal), beforeTotal, ...riskPotentialLabels.map(() => beforeTotal)];
    const afterData = [...compareFieldAllValues.map(() => 0), afterTotal, ...riskPotentialLabels.map(() => 0)];

    const compareData = compareFieldAllValues.reduce(
      (org, fieldValue) => {
        const fieldInfo: any = compareFieldInfos.find((info) => info[compareFieldKey] === fieldValue);
        if (fieldInfo) {
          if (fieldInfo.value >= 0) {
            org.increase.push(fieldInfo.value);
            org.decrease.push(0);
          } else {
            org.increase.push(0);
            org.decrease.push(-fieldInfo.value);
          }

          org.placeholder.push(org.lastValue);
          org.lastValue = fieldInfo.value + org.lastValue;
        }
        return org;
      },
      {
        lastValue: 0,
        placeholder: [],
        increase: [],
        decrease: [],
      }
    );

    const riskPotentialData =
      riskPotentialFields.length > 0
        ? riskCategories.reduce(
            (org, riskCategory) => {
              const riskCategoryData = gbRiskCategoryData[riskCategory];
              const total = sumBy(riskCategoryData, toCamelCase(riskPotentialFields[0].fieldKey));

              if (total >= 0) {
                org.increase.push(total);
                org.decrease.push(0);
              } else {
                org.increase.push(0);
                org.decrease.push(-total);
              }
              org.placeholder.push(org.lastValue);
              org.lastValue = total + org.lastValue;
              return org;
            },
            { lastValue: afterTotal, placeholder: [], increase: [], decrease: [] }
          )
        : { lastValue: afterTotal, placeholder: [], increase: [], decrease: [] };

    return {
      title: {
        value: `${fieldKpi.label || ''}リスク要因分解`,
        styles: {
          fontSize: fontSize.chartTitle,
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      chartOptions: {
        animation: false,
        xAxis: [
          {
            type: 'category',
            nameGap: 10,
            axisLabel: {
              interval: 0,
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },

            data: xAxisData,
          },
          {
            show: false,
            type: 'category',
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            data: xAxisData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: `値${fieldKpi.unitName ? `(${fieldKpi.unitName})` : ''}`,
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(value, fieldKpi.unitValue),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            scale: true,
          },
        ],
        grid: {
          top: 45,
          right: 60,
          bottom: 40,
          left: 85,
        },
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            lineStyle: {
              width: 2,
            },
          },
          formatter: toolTipFormatter,
        },
        series: [
          {
            name: 'placeholder',
            type: 'bar',
            stack: 'variation',
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            data: [...compareData.placeholder, 0, ...riskPotentialData.placeholder],
            label: {
              show: false,
              color: 'transparent',
            },
            barMaxWidth: 50,
          },
          {
            name: '未達分',
            type: 'bar',
            stack: 'variation',
            itemStyle: {
              color: color.errorColor,
            },
            data: [
              ...compareData.decrease,
              0,
              ...riskPotentialData.decrease.map((value) => ({
                type: 'riskPotential',
                value,
              })),
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.value ? `-${kpiValue(params.value, fieldKpi.unitValue)}` : ''),
              color: color.errorColor,
            },
            barMaxWidth: 50,
          },
          {
            name: '超過分',
            type: 'bar',
            stack: 'variation',
            itemStyle: {
              color: color.successColor,
            },
            data: [
              ...compareData.increase,
              0,
              ...riskPotentialData.increase.map((value) => ({
                type: 'riskPotential',
                value,
              })),
            ],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.value ? kpiValue(params.value, fieldKpi.unitValue) : ''),
              color: color.successColor,
            },
            barMaxWidth: 50,
          },
          {
            name: curLabel,
            type: 'bar',
            stack: 'variation',
            barGap: '-100%',
            data: afterData,
            itemStyle: {
              color: color.primaryColor,
            },
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => (params.value ? kpiValue(params.value, fieldKpi.unitValue) : ''),
            },
            barMaxWidth: 100,
          },
          {
            name: tarLabel,
            type: 'line',
            symbol: 'none',
            data: beforeData,
            itemStyle: {
              color: color.warningColor,
              borderType: 'dotted',
            },
            lineStyle: {
              type: 'dashed',
            },
            label: {
              show: false,
            },
            barMaxWidth: 100,
          },
        ],
      },
      height: 'calc(100% - 80px)',
    };
  }, [dataProvider, fieldKpi, tarLabel, curLabel, fontSize, nameSortShow]);

  const seriesClickHandler = (e) => {
    console.log(e);
    if ((e.seriesName === '未達分' || e.seriesName === '超過分') && !e.name.includes('その他') && e.data.type !== 'riskPotential') {
      const eventInfo = {
        eventType: GLOBAL_EVENT.CHART_ITEM_CLICK,
        data: { fieldKey: compareFieldKey, value: e.name },
        pageId: PAGE_ID.RISK,
      };
      event$.emit(eventInfo);
    }
  };

  return (
    <>
      <Card style={{ height: '100%' }} styles={{ body: { height: '100%', padding: 0 } }}>
        <ContentChart
          contentList={[]}
          extra={{
            value: (
              <Space size="large" style={{ marginRight: 12 }}>
                <Space>
                  <Flex align="center">内訳表示順: </Flex>
                  <Flex align="center">
                    <Switch
                      checkedChildren="名称順"
                      unCheckedChildren="差分の大きい順"
                      checked={nameSortShow}
                      style={{ background: color.primaryColor }}
                      onChange={(checked) => {
                        setNameSortShow(checked);
                        event$.emit({
                          eventType: GLOBAL_EVENT.DATA_SORT,
                          data: { sortKey: checked ? compareFieldKey : 'diff' },
                          pageId: PAGE_ID.COMPARE,
                        });
                      }}
                    />
                  </Flex>
                </Space>
              </Space>
            ),
          }}
          {...options}
          showSeriesLabel
          mouseWheelZoomLock={false}
          loading={chartLoading}
          height={'90%'}
          tooltips={['zoomIn']}
          isBank={!dataProvider || !dataProvider.currentData || dataProvider.currentData.length === 0}
          onClick={seriesClickHandler}
        />
      </Card>
    </>
  );
};

export default CustomizeRiskChart;
